import { ExternalLink, Droplets, Wind, Recycle, Leaf } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import { PUELineChart, EnergyBarChart, CoolingBars } from "@/components/charts";

const KPIS = [
  {
    code: "/ Capacity",
    value: "~100",
    unit: "GW added",
    label: "Global supply, 2026 to 2030",
    delta: "Total reaches ~200 GW",
    source: "JLL 2026",
  },
  {
    code: "/ US Demand",
    value: "366",
    unit: "TWh",
    label: "US DC electricity, 2025",
    delta: "→ 728 TWh by 2030",
    source: "S&P Global 2026",
  },
  {
    code: "/ Efficiency",
    value: "1.56",
    unit: "PUE",
    label: "Global average, flat 6th year",
    delta: "From 2.50 in 2007",
    source: "Uptime 2026",
  },
  {
    code: "/ Build Cost",
    value: "$11.3M",
    unit: "per MW",
    label: "Avg DC construction, 2026",
    delta: "+6% year over year",
    source: "JLL 2026",
  },
];

const LEVERS = [
  {
    Icon: Wind,
    code: "L.01",
    title: "Liquid and immersion cooling",
    body: "Moves heat far more efficiently than air. Direct-to-chip and immersion cut facility cooling energy 35 to 55% versus legacy raised-floor air, and unlock the 80 kW+ AI racks now standard in 2026 buildouts.",
  },
  {
    Icon: Leaf,
    code: "L.02",
    title: "Hyperscaler-led clean energy",
    body: "S&P Global 2026 reports the top four US hyperscalers account for more than 61% of tracked corporate clean energy procurement. Power purchase agreements remain the primary lever, with 24/7 carbon-free energy contracts expanding into 2026.",
  },
  {
    Icon: Recycle,
    code: "L.03",
    title: "Waste-heat reuse",
    body: "Northern European data centers feed district heating networks. Under the recast EU Energy Efficiency Directive, sites above 1 MW must use waste heat where feasible, with mandatory reporting fully in effect through 2026.",
  },
  {
    Icon: Droplets,
    code: "L.04",
    title: "Low-water cooling designs",
    body: "Air-side economization, adiabatic cooling, and emerging zero-water cooling architectures. New 2026 AI data center designs from leading operators are targeting near-zero water for cooling at hyperscale.",
  },
];

const SOURCES = [
  {
    label: "JLL · Global Data Center Outlook 2026",
    url: "https://www.jll.com/en-us/insights/market-outlook/data-center-outlook",
  },
  {
    label: "Goldman Sachs Research · Data center power demand (March 2026)",
    url: "https://www.goldmansachs.com/insights/goldman-sachs-research/how-rising-hyperscaler-reinvestment-impacts-power-growth-constraints",
  },
  {
    label: "Uptime Institute · 15th Annual Global Data Center Survey (2026)",
    url: "https://uptimeinstitute.com/about-ui/press-releases/uptimes-15th-annual-global-data-center-survey-results-shows-both-commitment-and-hesitancy",
  },
  {
    label: "S&P Global · 2026 Trends in Data Center Services and Infrastructure",
    url: "https://www.spglobal.com/energy/en/news-research/special-reports/energy-transition/2026-trends-in-data-center-services-infrastructure",
  },
  {
    label: "CBRE · Data Center 2026 Outlook",
    url: "https://www.cbre.com/insights/reports/global-data-center-trends-2025",
  },
  {
    label: "Accenture · Data Centre Trends 2026, shifting up a gear",
    url: "https://www.accenture.com/us-en/insights/infrastructure-capital-projects/data-centre-trends-2026-shifting-up-gear",
  },
  {
    label: "Schneider Electric · Mind the gap, AI talent shortages (Feb 2026)",
    url: "https://blog.se.com/datacenter/2026/02/25/mind-gap-bridging-ai-talent-shortages-data-centers/",
  },
  {
    label: "EU · Energy Efficiency Directive, mandatory DC reporting in force",
    url: "https://energy.ec.europa.eu/topics/energy-efficiency/energy-efficiency-targets-directive-and-rules/energy-efficiency-directive_en",
  },
];

export default function Sustainability() {
  return (
    <section
      id="sustainability"
      data-testid="sustainability-section"
      className="border-b hairline bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 lg:col-span-5">
            <div className="chrome-surface inline-flex items-center justify-center w-36 h-36 md:w-44 md:h-44 mb-8 p-4">
              <img
                src="/dcyb-logo.png"
                alt=""
                aria-hidden
                className="w-full h-full object-contain"
                draggable="false"
              />
            </div>
            <div data-testid="sustainability-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / Sustainability
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              Powering AI <span className="text-chrome">responsibly.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              {"The systems that power the AI era cannot cost the planet. Uptime 2026 confirms global PUE has held flat near 1.56 for the sixth straight year, even as power demand surges. The next decade is about doing more with less. Cleaner power, smarter cooling, and a workforce that knows how to build it."}
            </p>
            <p className="mt-4 font-mono-label text-[10px] text-[#71747B] leading-relaxed">
              All figures below are sourced. See bottom of section.
            </p>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t hairline mb-12">
          {KPIS.map((k) => (
            <div
              key={k.code}
              data-testid={`sustain-kpi-${k.code.replace(/[^a-zA-Z0-9]/g, "")}`}
              className="border-r border-b hairline p-6 md:p-7 bg-gradient-to-b from-white to-[#F4F5F7] hover:from-white hover:to-[#E8EAED] transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  {k.code}
                </span>
                <span className="font-mono-label text-[9px] text-[#71747B]">
                  {k.source}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <div className="font-display text-4xl md:text-5xl font-extrabold text-chrome tracking-tight">
                  {k.value}
                </div>
                <div className="font-mono-label text-[11px] text-[#0F1117]">
                  {k.unit}
                </div>
              </div>
              <div className="mt-3 text-[13px] text-[#3D4049] leading-snug">
                {k.label}
              </div>
              <div className="mt-2 font-mono-label text-[10px] text-[#71747B]">
                {k.delta}
              </div>
            </div>
          ))}
        </div>

        {/* Main chart panel — Energy demand */}
        <div className="chrome-surface relative p-6 md:p-8 mb-6">
          <CornerMarks color="#A9ADB4" />
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-2">
            <div>
              <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
                FIG. 01 · Global data-center capacity buildout
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-[#0F1117] tracking-tight">
                Global capacity doubles by 2030. AI is the driver.
              </h3>
              <p className="text-[13px] text-[#52555B] leading-relaxed mt-2 max-w-xl">
                Solid bars are reported. Striped bars are JLL projections. Reaching
                200 GW by 2030 means roughly 100 GW added across just five years.
              </p>
            </div>
            <div className="font-mono-label text-[10px] text-[#71747B] sm:text-right sm:shrink-0">
              Unit: GW
              <br />
              Source: JLL · Global DC Outlook 2026
            </div>
          </div>
          <EnergyBarChart className="w-full h-auto mt-2" />
        </div>

        {/* Two side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <div className="chrome-surface relative p-6 md:p-8">
            <CornerMarks color="#A9ADB4" />
            <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
              FIG. 02 · Industry-average PUE
            </div>
            <h3 className="font-display text-xl font-bold text-[#0F1117] tracking-tight mb-1">
              Each new watt does more work.
            </h3>
            <p className="text-[13px] text-[#52555B] leading-relaxed mb-3">
              Lower is better. Power Usage Effectiveness has fallen from 2.50 to 1.56
              in 17 years. Best-in-class hyperscalers run at 1.10. Uptime 2026 notes
              the global average has held flat for the sixth straight year.
            </p>
            <PUELineChart className="w-full h-auto" />
            <div className="font-mono-label text-[10px] text-[#71747B] mt-2">
              Source: Uptime Institute 15th Annual Global DC Survey, 2026
            </div>
          </div>

          <div className="chrome-surface relative p-6 md:p-8">
            <CornerMarks color="#A9ADB4" />
            <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
              FIG. 03 · Cooling energy by technology
            </div>
            <h3 className="font-display text-xl font-bold text-[#0F1117] tracking-tight mb-1">
              The cooling stack matters.
            </h3>
            <p className="text-[13px] text-[#52555B] leading-relaxed mb-3">
              Relative facility cooling energy, indexed to legacy raised-floor air at
              100. Liquid and immersion architectures cut cooling energy roughly in
              half.
            </p>
            <CoolingBars className="w-full h-auto" />
            <div className="font-mono-label text-[10px] text-[#71747B] mt-2">
              Source: Industry benchmarks, cross-referenced in 2026 outlooks
            </div>
          </div>
        </div>

        {/* Levers */}
        <div className="mb-12">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-6">
            / The levers we work with
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t hairline">
            {LEVERS.map(({ Icon, code, title, body }) => (
              <article
                key={code}
                data-testid={`sustain-lever-${code}`}
                className="border-r border-b hairline p-7 md:p-9 bg-gradient-to-b from-white to-[#F4F5F7] hover:from-white hover:to-[#E8EAED] transition-colors flex flex-col sm:flex-row gap-4 sm:gap-5"
              >
                <span className="chrome-tile w-12 h-12 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-[#0F1117]" />
                </span>
                <div className="min-w-0">
                  <div className="font-mono-label text-[10px] text-[#71747B] mb-1">
                    {code}
                  </div>
                  <h4 className="font-display text-lg sm:text-xl font-bold text-[#0F1117] tracking-tight break-words mb-2">
                    {title}
                  </h4>
                  <p className="text-[14px] text-[#52555B] leading-relaxed">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Sources */}
        <div className="chrome-surface p-6 md:p-8 relative">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-5">
            / Sources & further reading
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {SOURCES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`source-link-${s.url}`}
                  className="group flex items-center gap-2 text-[14px] text-[#3D4049] hover:text-[#0F1117] transition-colors border-b hairline pb-3"
                >
                  <span className="flex-1">{s.label}</span>
                  <ExternalLink
                    size={13}
                    className="text-[#71747B] group-hover:text-[#0F1117] transition-colors shrink-0"
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
